import { z } from 'zod/v4';

/**
 * Amfori sync payload contract — the matcher input.
 *
 * NOTE: data-points-lib 14.5.10 only adds the storable supplier-side profile
 * data-points; it does NOT provide these inbound `Site` / `BusinessPartner`
 * shapes. No equivalent zod schema exists elsewhere in the repo, so the
 * minimal contract the matcher consumes is defined here. Keep these schemas
 * limited to the fields `AmforiSupplierMatcherService` actually reads — the
 * full amfori API payload is much larger and is parsed at the sync-route layer.
 */

/**
 * A single amfori site (facility) being synced. The matcher only needs the
 * amfori site id (step 1, exact-id match) and the legal name (step 3, hybrid
 * name search).
 */
export const SiteSchema = z.object({
  /** Per-site amfori identifier, e.g. '792-000179-002'. */
  siteAmforiId: z.string(),
  /** Site legal name, used as the hybrid-search term. */
  legalName: z.string(),
  /**
   * ISO alpha-2 country of the site (from the amfori `/site` `address.country`).
   * Used only to break an ambiguous hybrid-search match (step 3) — the same
   * representation as the candidate company country, so they compare directly.
   * Absent when the amfori payload carries no address.
   */
  countryCode: z.string().nullish(),
});

export type Site = z.infer<typeof SiteSchema>;

/**
 * The parent business partner of a synced site. The matcher only consumes the
 * list of external company references (step 2, external-reference match).
 */
export const BusinessPartnerSchema = z.object({
  /**
   * Free-form external identifiers the partner is also known by (e.g. a
   * brand's internal vendor code). Each is matched case-insensitively and
   * trimmed against the network's OTHER_IDS answers. Optional / may be empty.
   */
  externalCompanyReferences: z.array(z.string()).optional(),
});

export type BusinessPartner = z.infer<typeof BusinessPartnerSchema>;

/** Full matcher input: one site plus its parent business partner. */
export const AmforiMatchInputSchema = z.object({
  site: SiteSchema,
  businessPartner: BusinessPartnerSchema,
});

export type AmforiMatchInput = z.infer<typeof AmforiMatchInputSchema>;
